import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  activateClientService,
  addClientService,
  deactivateClientService,
  downloadFileByIdService,
  downloadTemplateFileService,
  editClientService,
  getClientDetailsById,
  getClientService,
  getFileIdService,
  uploadBulkImportClientsService,
} from "../../services/crosspay-service";
import { AsyncAppThunk } from "../application-state";
import { addSnack, showLoaderWhile } from "./ui";

import { ClientModel, ClientRequestModel } from "libs/models/client";
import { PaginatedList } from "libs/models/paginated-list";
import { getFilterQuery } from "utils/filter";
import { EqualFilter, Filter } from "libs/models/filter";
import { validateAddClientState } from "pages/AddClient/add-client.policy";
import { CustomizedBack } from "store/navigation-wrapper";
import { validateEditClientState } from "pages/EditClient/edit-client.policy";
import { setCloseDialog } from "./modal-popup";
import translate from "utils/translations";
import { validateFileSize } from "utils/file-validation";

export interface ClientStore {
  isValidating: boolean;
  newClientData: ClientRequestModel;
  updateClientData: ClientModel;
  clientData: PaginatedList<ClientModel>;
  filter: Filter;
  searchingValue: string | undefined;
  logoId: string | null;
  commercialRegisterFiledId: string | null;
  taxRegistrationFileId: string | null;
}

const initialState: ClientStore = {
  isValidating: false,
  updateClientData: {
    id: "",
    name: "",
    phone: "",
    mobile: "",
    website: "",
    isActive: true,
    logo: "",
    taxRegistrationFile: "",
    commercialRegisterFiled: "",
    branchesNumber: 0,
    joinedAt: "",
    marginFees: "",
    isMarginFeesPercentage: false,
    disabled: false,
  } as ClientModel,

  newClientData: {
    name: "",
    phone: "",
    mobile: "",
    website: "",
    isActive: true,
    logo: "",
    taxRegistrationFile: "",
    commercialRegisterFiled: "",
    clickingFees: undefined,
    invoiceGenerationWeeklyPeriod: undefined,
    isClickingFeesPercentage: false,
  } as ClientRequestModel,
  clientData: { list: [], pageSize: 10, pageIndex: 0, totalCount: 0 },
  filter: {
    orderBy: { key: "CreatedAt", sort: "desc" },
  },
  searchingValue: undefined,
  logoId: null,
  taxRegistrationFileId: null,
  commercialRegisterFiledId: null,
};

const slice = createSlice({
  name: "client",
  initialState,
  reducers: {
    setMediasIdsChange: (
      state: ClientStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      if (
        [
          "commercialRegisterFiledId",
          "taxRegistrationFileId",
          "logoId",
        ].includes(key)
      ) {
        return { ...state, [key]: value };
      }
    },
    setClientsFetchingData: (
      state: ClientStore,
      action: PayloadAction<PaginatedList<ClientModel>>,
    ) => {
      state.clientData = action.payload;
    },
    setUpdateClientData: (
      state: ClientStore,
      action: PayloadAction<ClientModel>,
    ) => {
      state.updateClientData = action.payload;
      state.logoId = action.payload.logo;
      state.taxRegistrationFileId = action.payload.taxRegistrationFile;
      state.commercialRegisterFiledId = action.payload.commercialRegisterFiled;
    },
    setIsValidating: (state: ClientStore, action: PayloadAction<boolean>) => {
      state.isValidating = action.payload;
    },
    setEqualFilter: (
      state: ClientStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      if (value === null) {
        delete state.filter[key];
        return state;
      } else {
        state.filter = { ...state.filter, [key]: value! };
      }
      return state;
    },
    setClientViewDynamicSearchFilter: (
      state: ClientStore,
      action: PayloadAction<EqualFilter[]>,
    ) => {
      state.filter = { ...state.filter, or: action.payload };
    },
    setSearchKey: (state: ClientStore, action: PayloadAction<string>) => {
      state.searchingValue = action.payload;
    },
    setClientsPage: (state: ClientStore, action: PayloadAction<number>) => {
      state.clientData.pageIndex = action.payload;
    },
    setUpdateClientDataProps: (
      state: ClientStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      return {
        ...state,
        updateClientData: { ...state.updateClientData, [key]: value! },
      };
    },
    setNewClientData: (
      state: ClientStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      return {
        ...state,
        newClientData: { ...state.newClientData, [key]: value! },
      };
    },
    addingClient: (state: ClientStore) => {
      state.newClientData = initialState.newClientData;
      state.logoId = null;
      state.taxRegistrationFileId = null;
      state.commercialRegisterFiledId = null;
      state.isValidating = false;
    },
    editingClient: (state: ClientStore) => {
      state.updateClientData = initialState.updateClientData;
      state.logoId = null;
      state.taxRegistrationFileId = null;
      state.commercialRegisterFiledId = null;
    },
    clearFilters: (state: ClientStore) => {
      state.filter = initialState.filter;
      state.searchingValue = undefined;
    },
  },
});

export const {
  setClientsFetchingData,
  setEqualFilter,
  setClientViewDynamicSearchFilter,
  setSearchKey,
  setClientsPage,
  setNewClientData,
  addingClient,
  setIsValidating,
  setUpdateClientData,
  setUpdateClientDataProps,
  setMediasIdsChange,
  editingClient,
  clearFilters,
} = slice.actions;
export const reducer = slice.reducer;

export const getClientsData = (): AsyncAppThunk => async (dispatch, store) => {
  dispatch(
    showLoaderWhile(async () => {
      var clientsPaging = store().client.clientData;
      var queryOptions = getFilterQuery(
        store().client.filter,
        clientsPaging.pageIndex * clientsPaging.pageSize,
        clientsPaging.pageSize,
      );
      const apiResponse = await getClientService(queryOptions);
      if (
        apiResponse &&
        Object.keys(apiResponse).length > 0 &&
        apiResponse.result !== null
      ) {
        dispatch(setClientsFetchingData(apiResponse.result));
      }
    }),
  );
};
export const getClientsDataById =
  (id: string): AsyncAppThunk =>
  async (dispatch) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await getClientDetailsById(id);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setUpdateClientData(apiResponse.result));
        }
      }),
    );
  };

export const getFileId =
  (key: string, file: File): AsyncAppThunk =>
  async (dispatch, store) => {
    const validationError = validateFileSize(file);
    if (validationError) {
      dispatch(
        addSnack({
          message: validationError,
          color: "#B23B3B",
        }),
      );
      return;
    }
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await getFileIdService(file);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setMediasIdsChange({ key: key, value: apiResponse.result }));
        }
      }),
    );
  };
export const addClient = (): AsyncAppThunk => async (dispatch, store) => {
  var clientState = store().client;
  var requestModel = {
    ...clientState.newClientData,
    logo: clientState.logoId,
    commercialRegisterFiled: clientState.commercialRegisterFiledId,
    taxRegistrationFile: clientState.taxRegistrationFileId,
  } as ClientRequestModel;
  dispatch(setIsValidating(true));
  if (Object.keys(validateAddClientState(store().client)).length === 0) {
    dispatch(
      showLoaderWhile(async () => {
        dispatch(setIsValidating(false));
        const response = await addClientService(requestModel);
        if (
          response &&
          Object.keys(response).length > 0 &&
          response.result !== null
        ) {
          dispatch(
            addSnack({
              message: translate(
                "Client_Added_Successfully",
                "Client Added Successfully",
              ),
            }),
          );
          CustomizedBack();
          dispatch(addingClient());
        }
      }),
    );
  }
};

export const editClient = (): AsyncAppThunk => async (dispatch, store) => {
  var clientState = store().client;

  var updatedModel = {
    ...clientState.updateClientData,
    logo: clientState.logoId,
    commercialRegisterFiled: clientState.commercialRegisterFiledId,
    taxRegistrationFile: clientState.taxRegistrationFileId,
  } as ClientModel;
  dispatch(setIsValidating(true));
  if (Object.keys(validateEditClientState(store().client)).length === 0) {
    dispatch(
      showLoaderWhile(async () => {
        dispatch(setIsValidating(false));
        const response = await editClientService(updatedModel);
        if (
          response &&
          Object.keys(response).length > 0 &&
          response.result !== null
        ) {
          dispatch(
            addSnack({
              message: translate(
                "Client_Edited_Successfully",
                "Client Edited Successfully",
              ),
            }),
          );
          CustomizedBack();
          dispatch(editingClient());
        }
      }),
    );
  }
};
export const deactivateClient =
  (id: string): AsyncAppThunk =>
  async (dispatch, store) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await deactivateClientService(id);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(
            addSnack({
              message: translate(
                "Client_Deactivated_Successfully",
                "Client Deactivated Successfully",
              ),
            }),
          );
          dispatch(setCloseDialog());
          dispatch(getClientsDataById(id));
        }
      }),
    );
  };

export const activateClient =
  (id: string): AsyncAppThunk =>
  async (dispatch) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await activateClientService(id);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(
            addSnack({
              message: translate(
                "Client_Activated_Successfully",
                "Client Activated Successfully",
              ),
            }),
          );
          dispatch(setCloseDialog());
          dispatch(getClientsDataById(id));
        }
      }),
    );
  };
export const downloadTemplateFile = (): AsyncAppThunk => async (dispatch) => {
  dispatch(
    showLoaderWhile(async () => {
      const apiResponse = await downloadTemplateFileService();
      if (apiResponse && Object.keys(apiResponse).length > 0) {
        dispatch(
          addSnack({
            message: translate(
              "File_Downloaded_Text",
              "File Downloaded successfully",
            ),
          }),
        );
      }
    }),
  );
};
export const uploadBulkImportClients =
  (id: string, file: File): AsyncAppThunk =>
  async (dispatch) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await uploadBulkImportClientsService(id, file);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result === true
        ) {
          dispatch(
            addSnack({
              message: translate(
                "File_Uploaded_Text",
                "File Uploaded successfully",
              ),
            }),
          );
        }
      }),
    );
  };
export const downloadFileById =
  (id: string): AsyncAppThunk =>
  async (dispatch) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await downloadFileByIdService(id);
        if (apiResponse && Object.keys(apiResponse).length > 0) {
          dispatch(
            addSnack({
              message: translate(
                "File_Downloaded_Text",
                "File Downloaded successfully",
              ),
            }),
          );
        }
      }),
    );
  };
